import { useRef } from "react";
import type { TextInput } from "react-native";
import { XStack, YStack } from "tamagui";

import { Paragraph } from "@/components";
import {
  CheckboxBadge,
  type ChoiceValue,
  OptionBody,
  OptionBodyHoverWrapper,
  OptionSeparator,
  OtherTextInput,
  QuestionScrollArea,
} from "@/conversation/AskUserQuestionsPanel/OptionParts";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";

function OtherRow({
  value,
  onChangeText,
}: {
  value: string;
  onChangeText: (value: string) => void;
}) {
  const inputRef = useRef<TextInput>(null);
  const active = value.trim().length > 0;
  return (
    <OptionBodyHoverWrapper>
      <XStack
        flex={1}
        alignItems="center"
        paddingHorizontal="$150"
        paddingVertical="$200"
        gap="$100"
        cursor="text"
        onPress={() => (active ? onChangeText("") : inputRef.current?.focus())}
      >
        <CheckboxBadge checked={active} />
        <OtherTextInput
          value={value}
          onChangeText={onChangeText}
          inputRef={inputRef}
        />
      </XStack>
    </OptionBodyHoverWrapper>
  );
}

export default function MultipleChoiceQuestion({
  options,
  value,
  onChange,
  onSubmit,
  onSkip,
  isLast,
  allowOther = true,
  allowSkip = true,
  dataTrackingId,
}: {
  options: string[];
  value: ChoiceValue;
  onChange: (value: ChoiceValue) => void;
  onSubmit: () => void;
  onSkip: () => void;
  isLast: boolean;
  /** Show the free-text "Enter answer here" row. Off for constrained flows (e.g. rx). */
  allowOther?: boolean;
  /** Show the Skip control. Off for required flows (e.g. rx). */
  allowSkip?: boolean;
  dataTrackingId?: string;
}) {
  const toggle = (option: string) => {
    const selected = value.selected.includes(option)
      ? value.selected.filter((s) => s !== option)
      : [...value.selected, option];
    onChange({ ...value, selected });
  };
  const count = value.selected.length + (value.otherText.trim() ? 1 : 0);

  return (
    <>
      <QuestionScrollArea>
        {options.map((option, index) => (
          <YStack key={option}>
            {index > 0 && <OptionSeparator />}
            <OptionBodyHoverWrapper>
              <OptionBody
                label={option}
                checked={value.selected.includes(option)}
                dataTrackingId={dataTrackingId}
                badge={
                  <CheckboxBadge checked={value.selected.includes(option)} />
                }
                onPress={() => toggle(option)}
              />
            </OptionBodyHoverWrapper>
          </YStack>
        ))}
        {allowOther && (
          <>
            <OptionSeparator />
            <OtherRow
              value={value.otherText}
              onChangeText={(otherText) => onChange({ ...value, otherText })}
            />
          </>
        )}
      </QuestionScrollArea>
      <OptionSeparator />
      <XStack
        alignItems="center"
        gap="$200"
        paddingHorizontal="$150"
        paddingBottom="$150"
        marginTop="$250"
      >
        <Paragraph flex={1} size="$xs" color="$textSecondary">
          {count} selected
        </Paragraph>
        <SkipOrSubmit
          showSkip={allowSkip}
          showSubmit
          submitDisabled={count === 0}
          isLast={isLast}
          onSkip={onSkip}
          onSubmit={onSubmit}
        />
      </XStack>
    </>
  );
}
